import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n/I18nProvider";
import { moroccoRegions, majorParties, otherParties } from "@/data/parties";
import { CheckCircle, Clock, Users, MapPin, BarChart2, TrendingUp, RefreshCw, Briefcase, ShieldCheck } from "lucide-react";
import {
  AGE_TRANCHES, CATEGORIES_SOCIOPRO
} from "@/data/accreditationStore";
import {
  computeCandidatesBarometerData, computeCandidateProvinceStats,
  BarometerPartyStats, BarometerRegionStats, useCandidatesStore, fetchPublicCandidates
} from "@/data/candidatesStore";
import { useActiveAds } from "@/hooks/useActiveAds";
import { getBarometerMode, isBarometerEnabled, type BarometerMode } from "@/utils/barometerMode";
import { getBarometerConfig, type BarometerConfig } from "@/utils/barometerConfigStore";
import ElectionResultsBarometer from "./ElectionResultsBarometer";

// ─── Party color map ─────────────────────────────────────────────────────────
const PARTY_COLORS: Record<string, string> = {
  RNI: "#3b82f6", PAM: "#f59e0b", PI: "#10b981", USFP: "#ef4444",
  MP: "#84cc16", PPS: "#dc2626", UC: "#0ea5e9", PJD: "#f97316",
};
const AGE_COLORS = ["#6366f1", "#8b5cf6", "#a78bfa", "#c4b5fd", "#ddd6fe"];
const SOCIOPRO_COLORS = [
  "#3b82f6","#10b981","#f59e0b","#84cc16","#f97316",
  "#8b5cf6","#ec4899","#0ea5e9","#dc2626","#6b7280",
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProgressBar({ value, max, color = "#6366f1" }: { value: number; max: number; color?: string }) {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div className="relative w-full h-3 bg-muted/50 rounded-full overflow-hidden">
      <motion.div
        className="absolute left-0 top-0 h-full rounded-full"
        style={{ background: `linear-gradient(90deg, ${color}cc, ${color})` }}
        initial={{ width: 0 }}
        whileInView={{ width: `${pct}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: "easeOut" }}
      />
    </div>
  );
}

function RadialGauge({ pct, color, size = 80 }: { pct: number; color: string; size?: number }) {
  const r = (size - 14) / 2;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--border))" strokeWidth="7" />
        <motion.circle
          cx={size / 2} cy={size / 2} r={r}
          fill="none" stroke={color} strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={`${circ}`}
          initial={{ strokeDashoffset: circ }}
          whileInView={{ strokeDashoffset: circ - dash }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="font-display font-black text-sm" style={{ color }}>{pct}%</span>
      </div>
    </div>
  );
}

function StatusBadge({ pct, lang = 'fr' }: { pct: number, lang?: string }) {
  if (pct >= 70) return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{lang === 'ar' ? '✓ قوي' : '✓ Fort'}</span>;
  if (pct >= 40) return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{lang === 'ar' ? '⚡ متوسط' : '⚡ Moyen'}</span>;
  return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">{lang === 'ar' ? '⚠ ضعيف' : '⚠ Faible'}</span>;
}

// ─── Tab: Par Parti ─────────────────────────────────────────────────────────
function ByPartyTab({ data, onClose, lang }: { data: BarometerPartyStats[], onClose: () => void, lang: string }) {
  const [selectedParty, setSelectedParty] = useState<string>("_all_");
  
  const barData = data.map(d => ({
    party: d.partyAcronym,
    pct: Math.round((d.accredited / 104) * 100),
    pctLocale: Math.round((d.accreditedLocale / 92) * 100),
    pctRegionale: Math.round((d.accreditedRegionale / 12) * 100),
    accLocale: d.accreditedLocale,
    accRegionale: d.accreditedRegionale,
    accredited: d.accredited, total: 104,
  }));

  const filteredData = selectedParty === "_all_" ? barData : barData.filter(d => d.party === selectedParty);

  const getPartyDisplay = (acronym: string) => {
    const p = majorParties.find(mp => mp.acronym === acronym) || otherParties.find(op => op.acronym === acronym);
    return lang === 'ar' && (p as { acronymAr?: string })?.acronymAr ? `${(p as { acronymAr?: string }).acronymAr}` : acronym;
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-sm font-display font-bold text-foreground flex items-center gap-2 shrink-0">
          <CheckCircle className="w-4 h-4 text-primary" />{lang === 'ar' ? 'تصفية حسب الحزب السياسي' : 'Filtre par parti politique'}
        </p>
        <Select value={selectedParty} onValueChange={setSelectedParty}>
          <SelectTrigger className="h-9 rounded-xl text-sm bg-background w-full sm:w-64">
            <SelectValue placeholder={lang === 'ar' ? 'جميع الأحزاب' : 'Tous les partis'} />
          </SelectTrigger>
          <SelectContent className="max-h-[280px]">
            <SelectItem value="_all_">{lang === 'ar' ? `جميع الأحزاب (${barData.length})` : `Tous les partis (${barData.length})`}</SelectItem>
            {barData.map(d => <SelectItem key={d.party} value={d.party}>{getPartyDisplay(d.party)}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {filteredData.map(({ party, pct, accLocale, accRegionale }) => {
          const color = PARTY_COLORS[party] || "#6366f1";
          return (
            <motion.div
              key={party}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4 }}
              className="p-4 bg-card rounded-2xl border border-border shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="px-2.5 py-1 rounded-lg text-xs font-display font-black text-white" style={{ background: color }}>{getPartyDisplay(party)}</div>
                <RadialGauge pct={pct} color={color} size={48} />
              </div>
              <div className="space-y-3 mt-2 border-t border-border/50 pt-2">
                <div>
                  <div className="flex justify-between items-center text-[10px] mb-1">
                    <span className="text-muted-foreground font-semibold">{lang === 'ar' ? 'محلية' : 'Locales'}</span>
                    <span className="font-mono font-bold text-foreground">{accLocale}/92</span>
                  </div>
                  <ProgressBar value={accLocale} max={92} color={color} />
                </div>
                <div>
                  <div className="flex justify-between items-center text-[10px] mb-1">
                    <span className="text-muted-foreground font-semibold">{lang === 'ar' ? 'جهوية' : 'Régionales'}</span>
                    <span className="font-mono font-bold text-foreground">{accRegionale}/12</span>
                  </div>
                  <ProgressBar value={accRegionale} max={12} color="#ec4899" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
        <p className="text-sm font-display font-bold text-foreground mb-4 flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-primary" />{lang === 'ar' ? 'التغطية حسب الأحزاب' : 'Couverture accréditée par parti (%)'}
        </p>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 40, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="hsl(var(--border))" />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `${v}%`} />
              <YAxis dataKey="party" type="category" tick={{ fontSize: 11, fontWeight: 700 }} axisLine={false} tickLine={false} width={40} />
              <Tooltip formatter={(v: number, n: string) => [`${v}%`, n]} contentStyle={{ borderRadius: 10, border: "none", fontSize: 11 }} />
              <Legend iconSize={8} wrapperStyle={{ fontSize: 10, paddingTop: "10px" }} />
              <Bar dataKey="pctLocale" name={lang === 'ar' ? 'محلية (%)' : 'Locales (%)'} fill="#6366f1" radius={[0, 4, 4, 0]} maxBarSize={14} />
              <Bar dataKey="pctRegionale" name={lang === 'ar' ? 'جهوية (%)' : 'Régionales (%)'} fill="#ec4899" radius={[0, 4, 4, 0]} maxBarSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
        <p className="text-sm font-display font-bold text-foreground mb-3">{lang === 'ar' ? 'وضعية الترشيحات' : "Statistiques des candidats"}</p>
        <div className="grid grid-cols-1 gap-3">
          {[
            { label: lang === 'ar' ? 'مرشح معتمد' : "Candidats Inscrits", value: data.reduce((s,d)=>s+d.accredited,0), color:"text-emerald-600", bg:"bg-emerald-50" },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className={`${bg} rounded-xl p-3 text-center`}>
              <p className={`text-2xl font-display font-black ${color}`}>{value}</p>
              <p className="text-[10px] font-semibold text-muted-foreground mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center mt-6 pb-2">
        <button onClick={onClose} className="px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-2xl font-bold text-sm shadow-sm transition-all border border-border flex items-center gap-2">
          {lang === 'ar' ? 'الغاء الاطلاع' : 'Voir moins'}
        </button>
      </div>
    </div>
  );
}

// ─── Tab: Par Région ─────────────────────────────────────────────────────────
function ByRegionTab({ data, onClose, lang }: { data: BarometerRegionStats[], onClose: () => void, lang: string }) {
  const [selectedRegion, setSelectedRegion] = useState<string>("");

  const regionStats = data.map(r => {
    const rData = moroccoRegions.find(mr => mr.name === r.region);
    const fullNameFr = r.region;
    const fullNameAr = rData?.nameAr || r.region;
    return {
      ...r,
      displayName: lang === 'ar' ? fullNameAr : fullNameFr,
      pct: r.total > 0 ? Math.round((r.accredited / r.total) * 100) : 0,
    };
  });

  const selectedData = selectedRegion ? data.find(r => r.region === selectedRegion) : null;
  const candidates = useCandidatesStore(s => s.candidates);
  const provinceStats = selectedRegion ? computeCandidateProvinceStats(selectedRegion, candidates) : [];

  const getPartyDisplay = (acronym: string) => {
    const p = majorParties.find(mp => mp.acronym === acronym) || otherParties.find(op => op.acronym === acronym);
    return lang === 'ar' && (p as { acronymAr?: string })?.acronymAr ? `${(p as { acronymAr?: string }).acronymAr}` : acronym;
  };

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
        <p className="text-sm font-display font-bold text-foreground mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4 text-primary" />{lang === 'ar' ? "التغطية الشاملة — 12 جهة" : "Couverture globale — 12 Régions"}
        </p>
        <div className="space-y-2.5">
          {regionStats.map(({ region, displayName, pct, accredited, total }) => (
            <div
              key={region}
              className={`flex items-center gap-3 cursor-pointer rounded-xl p-2 transition-colors ${selectedRegion === region ? "bg-primary/5 border border-primary/20" : "hover:bg-muted/30"}`}
              onClick={() => setSelectedRegion(selectedRegion === region ? "" : region)}
            >
              <div className="w-40 md:w-48 shrink-0"><span className={`text-[11px] font-body font-semibold truncate block ${selectedRegion === region ? "text-primary" : "text-foreground"}`} title={displayName}>{displayName}</span></div>
              <div className="flex-1"><ProgressBar value={accredited} max={total} color={pct >= 70 ? "#10b981" : pct >= 40 ? "#f59e0b" : "#ef4444"} /></div>
              <span className="text-[10px] text-muted-foreground w-16 text-right font-mono">{accredited}/{total}</span>
              <span className={`text-xs font-black w-10 text-right ${pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-red-600"}`}>{pct}%</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <p className="text-sm font-display font-bold text-foreground flex items-center gap-2 shrink-0">
            <TrendingUp className="w-4 h-4 text-indigo-500" />{lang === 'ar' ? 'التفاصيل حسب الأقاليم' : 'Détail par Province'}
          </p>
          <Select value={selectedRegion || "_none_"} onValueChange={v => setSelectedRegion(v === "_none_" ? "" : v)}>
            <SelectTrigger className="h-9 rounded-xl text-sm bg-background w-full sm:w-64">
              <SelectValue placeholder={lang === 'ar' ? 'اختر جهة...' : 'Sélectionnez une région…'} />
            </SelectTrigger>
            <SelectContent className="max-h-[280px]">
              <SelectItem value="_none_">{lang === 'ar' ? '— اختر جهة —' : '— Choisir une région —'}</SelectItem>
              {moroccoRegions.map(r => <SelectItem key={r.name} value={r.name}>{lang === 'ar' && r.nameAr ? r.nameAr : r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {!selectedRegion ? (
          <div className="text-center py-10 bg-muted/20 rounded-xl border border-dashed border-border">
            <MapPin className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm font-semibold text-muted-foreground">{lang === 'ar' ? 'انقر على جهة أو استخدم القائمة' : 'Cliquez sur une région ou utilisez le menu'}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {provinceStats.map(({ province, accredited, total, hommes, femmes }) => {
              const pct = total > 0 ? Math.round((accredited / total) * 100) : 0;
              return (
                <div key={province} className="flex items-center gap-3">
                  <div className="w-28 shrink-0"><span className="text-[11px] font-body text-foreground truncate block">{province.split("-")[0].trim()}</span></div>
                  <div className="flex-1"><ProgressBar value={accredited} max={Math.max(total, 1)} color={pct >= 70 ? "#10b981" : pct >= 40 ? "#f59e0b" : "#ef4444"} /></div>
                  <span className="text-[10px] text-blue-600 font-mono w-8">♂{hommes}</span>
                  <span className="text-[10px] text-pink-600 font-mono w-8">♀{femmes}</span>
                  <span className={`text-xs font-black w-10 text-right ${pct >= 70 ? "text-emerald-600" : pct >= 40 ? "text-amber-600" : "text-red-600"}`}>{pct}%</span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {selectedData && (
        <div className="bg-card border border-border rounded-2xl p-4 shadow-sm overflow-x-auto">
          <p className="text-sm font-display font-bold text-foreground mb-3">{lang === 'ar' ? 'الدوائر × الأحزاب' : 'Matrice Partis × Circonscriptions'} — {selectedData.region.split("-")[0]}</p>
          <div className="space-y-2 min-w-[400px]">
            {majorParties.map(party => {
              const d = selectedData.byParty[party.acronym] || { total: 0, accredited: 0 };
              const pct = d.total > 0 ? Math.round((d.accredited / d.total) * 100) : 0;
              const color = PARTY_COLORS[party.acronym] || "#6366f1";
              return (
                <div key={party.acronym} className="flex items-center gap-3">
                  <div className="w-12 shrink-0 px-2 py-0.5 rounded-md text-[10px] font-display font-black text-white text-center" style={{ background: color }}>{getPartyDisplay(party.acronym)}</div>
                  <div className="flex-1"><ProgressBar value={d.accredited} max={Math.max(d.total, 1)} color={color} /></div>
                  <span className="text-[10px] font-mono text-muted-foreground w-14 text-right">{d.accredited}/{d.total}</span>
                  <span className="text-xs font-black w-10 text-right" style={{ color }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="flex justify-center mt-6 pb-2">
        <button onClick={onClose} className="px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-2xl font-bold text-sm shadow-sm transition-all border border-border flex items-center gap-2">
          {lang === 'ar' ? 'الغاء الاطلاع' : 'Voir moins'}
        </button>
      </div>
    </div>
  );
}

function GenderTab({ data, onClose, lang }: { data: BarometerPartyStats[], onClose: () => void, lang: string }) {
  const totalH = data.reduce((s, d) => s + d.hommes, 0);
  const totalF = data.reduce((s, d) => s + d.femmes, 0);
  const total = totalH + totalF;
  const femPct = total > 0 ? Math.round((totalF / total) * 100) : 0;

  const getPartyDisplay = (acronym: string) => {
    const p = majorParties.find(mp => mp.acronym === acronym) || otherParties.find(op => op.acronym === acronym);
    return lang === 'ar' && (p as { acronymAr?: string })?.acronymAr ? `${(p as { acronymAr?: string }).acronymAr}` : acronym;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200/50 rounded-2xl p-4 text-center">
          <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-1">♂ {lang === 'ar' ? 'ذكور' : 'Hommes'}</p>
          <p className="text-2xl font-display font-black text-blue-600">{totalH}</p>
          <p className="text-xs text-blue-500">{100 - femPct}%</p>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-pink-50 dark:from-blue-950/20 dark:to-pink-950/20 border border-border rounded-2xl p-4 text-center flex flex-col items-center justify-center">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">{lang === 'ar' ? 'المناصفة' : 'Parité'}</p>
          <div className="relative w-16 h-16">
            <PieChart width={64} height={64}>
              <Pie data={[{name:"H",value:totalH,fill:"#3b82f6"},{name:"F",value:totalF,fill:"#ec4899"}]} cx={28} cy={28} innerRadius={18} outerRadius={28} dataKey="value" paddingAngle={3}>
                <Cell fill="#3b82f6" /><Cell fill="#ec4899" />
              </Pie>
            </PieChart>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[10px] font-black text-pink-600">{femPct}%F</span>
            </div>
          </div>
        </div>
        <div className="bg-pink-50 dark:bg-pink-950/30 border border-pink-200/50 rounded-2xl p-4 text-center">
          <p className="text-[10px] font-semibold text-pink-400 uppercase tracking-wider mb-1">♀ {lang === 'ar' ? 'إناث' : 'Femmes'}</p>
          <p className="text-2xl font-display font-black text-pink-600">{totalF}</p>
          <p className="text-xs text-pink-500">{femPct}%</p>
        </div>
      </div>
      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
        <p className="text-sm font-display font-bold text-foreground mb-4 flex items-center gap-2">
          <Users className="w-4 h-4 text-pink-500" />{lang === 'ar' ? 'توزيع الجنسين حسب الحزب' : 'Répartition genre par parti'}
        </p>
        <div className="space-y-3">
          {data.map(d => {
            const tot = d.hommes + d.femmes;
            const fPct = tot > 0 ? Math.round((d.femmes / tot) * 100) : 0;
            const color = PARTY_COLORS[d.partyAcronym] || "#6366f1";
            return (
              <div key={d.partyAcronym} className="flex items-center gap-3">
                <div className="w-12 shrink-0 text-center px-1.5 py-0.5 rounded-md text-[10px] font-display font-black text-white" style={{ background: color }}>{getPartyDisplay(d.partyAcronym)}</div>
                <div className="flex-1 flex h-5 rounded-full overflow-hidden">
                  <motion.div className="h-full bg-blue-400" initial={{ width: 0 }} whileInView={{ width: `${100 - fPct}%` }} viewport={{ once: true }} transition={{ duration: 0.8 }} />
                  <motion.div className="h-full bg-pink-400" initial={{ width: 0 }} whileInView={{ width: `${fPct}%` }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }} />
                </div>
                <span className="text-[10px] text-blue-600 font-mono w-8 text-right">{100-fPct}%♂</span>
                <span className="text-[10px] text-pink-600 font-mono w-8 text-right">{fPct}%♀</span>
                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full w-14 text-center ${fPct >= 33 ? "bg-emerald-100 text-emerald-700" : "bg-orange-100 text-orange-700"}`}>
                  {fPct >= 33 ? "✓ Quota" : "⚠️ <33%"}
                </span>
              </div>
            );
          })}
        </div>
        <div className="mt-3 flex items-center gap-4 text-[10px] text-muted-foreground flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-400 inline-block" />{lang === 'ar' ? 'ذكور' : 'Hommes'}</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-pink-400 inline-block" />{lang === 'ar' ? 'إناث' : 'Femmes'}</span>
          <span className="flex items-center gap-1 ml-auto text-amber-600 font-semibold">⚠ {lang === 'ar' ? 'الحصة القانونية = 33% نساء' : 'Quota légal = 33% femmes'}</span>
        </div>
      </div>
      <div className="flex justify-center mt-6 pb-2">
        <button onClick={onClose} className="px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-2xl font-bold text-sm shadow-sm transition-all border border-border flex items-center gap-2">
          {lang === 'ar' ? 'الغاء الاطلاع' : 'Voir moins'}
        </button>
      </div>
    </div>
  );
}

function AgeTab({ globalAge, onClose, lang }: { globalAge: Record<string, number>, onClose: () => void, lang: string }) {
  const total = Object.values(globalAge).reduce((s, v) => s + v, 0);
  const ageData = AGE_TRANCHES.map((tranche, i) => ({
    tranche, value: globalAge[tranche] || 0, fill: AGE_COLORS[i],
    pct: total > 0 ? Math.round(((globalAge[tranche] || 0) / total) * 100) : 0,
  }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-5 gap-2">
        {ageData.map(({ tranche, value, fill, pct }) => (
          <motion.div key={tranche} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="flex flex-col items-center p-3 rounded-2xl border border-border bg-card shadow-sm">
            <RadialGauge pct={pct} color={fill} size={52} />
            <span className="mt-2 text-[10px] font-display font-bold text-foreground">{tranche}</span>
            <span className="text-[9px] text-muted-foreground">{value} {lang === 'ar' ? 'مرشح' : 'cand.'}</span>
          </motion.div>
        ))}
      </div>
      <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
        <p className="text-sm font-display font-bold text-foreground mb-4 flex items-center gap-2">
          <BarChart2 className="w-4 h-4 text-indigo-500" />{lang === 'ar' ? 'التوزيع حسب الفئة العمرية' : "Distribution par tranche d'âge"}
        </p>
        <div className="h-[200px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ageData} margin={{ top: 4, right: 10, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="tranche" axisLine={false} tickLine={false} tick={{ fontSize: 11 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "none", fontSize: 11 }} />
              <Bar dataKey="value" name={lang === 'ar' ? 'مرشح' : 'Candidats'} radius={[6, 6, 0, 0]} maxBarSize={48}>
                {ageData.map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-4 space-y-2 border-t border-border pt-4">
          {ageData.map(({ tranche, value, fill, pct }) => (
            <div key={tranche} className="flex items-center gap-3">
              <span className="text-[11px] font-body font-semibold text-foreground w-14 shrink-0">{tranche}</span>
              <div className="flex-1"><ProgressBar value={value} max={Math.max(total, 1)} color={fill} /></div>
              <span className="text-[10px] font-mono text-muted-foreground w-20 text-right">{value} ({pct}%)</span>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-center mt-6 pb-2">
        <button onClick={onClose} className="px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-2xl font-bold text-sm shadow-sm transition-all border border-border flex items-center gap-2">
          {lang === 'ar' ? 'الغاء الاطلاع' : 'Voir moins'}
        </button>
      </div>
    </div>
  );
}

function SocioProTab({ globalSociopro, onClose, lang }: { globalSociopro: Record<string, number>, onClose: () => void, lang: string }) {
  const socDict: Record<string, string> = {
    "Fonctionnaire / Agent public": "موظف / وكيل عمومي",
    "Enseignant / Cadre de l'éducation": "مدرس / إطار تربوي",
    "Professions libérales (médecin, avocat, ingénieur…)": "مهن حرة (طبيب، محام، مهندس...)",
    "Agriculteur / Éleveur": "فلاح / مربي ماشية",
    "Commerçant / Artisan": "تاجر / حرفي",
    "Chef d'entreprise / Manager": "رئيس مقاولة / مسير",
    "Étudiant": "طالب",
    "Retraité": "متقاعد",
    "Sans emploi": "عاطل عن العمل",
    "Autre": "أخرى"
  };
  const getSocLabel = (cat: string) => lang === 'ar' ? (socDict[cat] || cat) : cat;

  const total = Object.values(globalSociopro).reduce((s, v) => s + v, 0);
  const chartData = CATEGORIES_SOCIOPRO.map((cat, i) => ({
    cat: getSocLabel(cat).split(" (")[0].split(" / ")[0],
    fullCat: getSocLabel(cat),
    value: globalSociopro[cat] || 0,
    fill: SOCIOPRO_COLORS[i % SOCIOPRO_COLORS.length],
    pct: total > 0 ? Math.round(((globalSociopro[cat] || 0) / total) * 100) : 0,
  })).sort((a, b) => b.value - a.value);

  const pieData = chartData.filter(d => d.value > 0);

  return (
    <div className="space-y-4">
      {/* KPI top 3 */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {chartData.slice(0, 3).map(({ fullCat, value, fill, pct }, i) => (
          <motion.div
            key={fullCat}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            className="p-4 rounded-2xl border border-border bg-card shadow-sm flex items-center gap-4"
          >
            <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 text-white font-display font-black text-lg" style={{ background: fill }}>
              #{i + 1}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-body font-semibold text-foreground line-clamp-2 leading-tight">{fullCat}</p>
              <p className="text-xl font-display font-black mt-1" style={{ color: fill }}>{value} <span className="text-xs text-muted-foreground font-normal">({pct}%)</span></p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pie + Bars */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
          <p className="text-sm font-display font-bold text-foreground mb-3 flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-violet-500" />{lang === 'ar' ? 'التوزيع (مبيان)' : 'Répartition (donut)'}
          </p>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="45%" innerRadius={55} outerRadius={95} paddingAngle={3} dataKey="value" nameKey="fullCat">
                  {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip formatter={(v: number, name: string) => [`${v} (${total > 0 ? Math.round((v/total)*100) : 0}%)`, name]} contentStyle={{ borderRadius: 10, border: "none", fontSize: 10 }} />
                <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 9 }} formatter={(v) => v.split(" (")[0].split(" / ")[0]} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
          <p className="text-sm font-display font-bold text-foreground mb-3 flex items-center gap-2">
            <BarChart2 className="w-4 h-4 text-violet-500" />{lang === 'ar' ? 'الترتيب حسب العدد' : 'Classement par effectif'}
          </p>
          <div className="space-y-2.5 overflow-y-auto max-h-[280px]">
            {chartData.map(({ fullCat, cat, value, fill, pct }) => (
              <div key={fullCat} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ background: fill }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-body font-semibold text-foreground truncate">{cat}</p>
                  <div className="mt-0.5"><ProgressBar value={value} max={Math.max(total, 1)} color={fill} /></div>
                </div>
                <span className="text-[10px] font-mono text-muted-foreground w-16 text-right shrink-0">{value} ({pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Full table */}
      <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30">
          <p className="text-sm font-display font-bold text-foreground flex items-center gap-2">
            <Briefcase className="w-4 h-4 text-violet-500" />{lang === 'ar' ? 'الجدول الكامل — الفئات السوسيو-مهنية' : 'Tableau complet — Catégories socioprofessionnelles'}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-muted-foreground font-semibold uppercase">
              <tr>
                <th className="px-4 py-3 text-left">#</th>
                <th className="px-4 py-3 text-left">{lang === 'ar' ? 'الفئة' : 'Catégorie'}</th>
                <th className="px-4 py-3 text-right">{lang === 'ar' ? 'المرشحون' : 'Candidats'}</th>
                <th className="px-4 py-3 text-right">%</th>
              </tr>
            </thead>
            <tbody>
              {chartData.map(({ fullCat, value, fill, pct }, i) => (
                <tr key={fullCat} className="border-t border-border/50 hover:bg-muted/10 transition-colors">
                  <td className="px-4 py-2.5">
                    <span className="w-5 h-5 rounded-full inline-flex items-center justify-center text-[10px] font-bold text-white" style={{ background: fill }}>{i + 1}</span>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-foreground">{fullCat}</td>
                  <td className="px-4 py-2.5 text-right font-mono font-bold" style={{ color: fill }}>{value}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold bg-muted text-foreground">{pct}%</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-center mt-6 pb-2">
        <button onClick={onClose} className="px-8 py-3 bg-secondary hover:bg-secondary/80 text-secondary-foreground rounded-2xl font-bold text-sm shadow-sm transition-all border border-border flex items-center gap-2">
          Voir moins
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const TABS = [
  { id: "party",    label: "Par Parti",         labelAr: "حسب الحزب",       icon: CheckCircle },
  { id: "region",   label: "Par Région",        labelAr: "حسب الجهة",       icon: MapPin },
  { id: "gender",   label: "Genre",             labelAr: "مؤشر الجنس",      icon: Users },
  { id: "age",      label: "Âge",               labelAr: "مؤشر العمر",      icon: Clock },
  { id: "sociopro", label: "Catégorie Sociopro",labelAr: "الفئة المهنية",   icon: Briefcase },
];

const AccreditationBarometers = () => {
  const { lang } = useI18n();
  const isRtl = lang === "ar";
  const [activeTab, setActiveTab] = useState("");
  const { candidates, setCandidates } = useCandidatesStore();
  const [barometerData, setBarometerData] = useState(() => computeCandidatesBarometerData(candidates));
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [mode, setMode] = useState<BarometerMode>(getBarometerMode());
  const [isEnabled, setIsEnabled] = useState(isBarometerEnabled());
  const [config, setConfig] = useState<BarometerConfig>(getBarometerConfig());

  const refresh = async () => { 
    const data = await fetchPublicCandidates();
    setCandidates(data);
    setLastUpdate(new Date()); 
  };

  useEffect(() => {
    setBarometerData(computeCandidatesBarometerData(candidates));
  }, [candidates]);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 30000);
    const handleModeChange = () => setMode(getBarometerMode());
    const handleEnabledChange = (e: any) => setIsEnabled(e.detail.enabled);
    const handleConfigChange = (e: any) => setConfig(e.detail);
    window.addEventListener("barometerModeChanged", handleModeChange);
    window.addEventListener("barometerEnabledChanged", handleEnabledChange);
    window.addEventListener("barometerConfigChanged", handleConfigChange);
    return () => {
      clearInterval(interval);
      window.removeEventListener("barometerModeChanged", handleModeChange);
      window.removeEventListener("barometerEnabledChanged", handleEnabledChange);
      window.removeEventListener("barometerConfigChanged", handleConfigChange);
    };
  }, []);


  const handleCloseTab = () => {
    setActiveTab("");
    document.getElementById("accreditation-barometers-section")?.scrollIntoView({ behavior: "smooth" });
  };

  if (!isEnabled) return null;

  if (mode === "election") {
    return <ElectionResultsBarometer />;
  }

  return (
    <section id="accreditation-barometers-section" className="py-8 bg-background" dir={isRtl ? "rtl" : "ltr"}>
      <div className="container px-4 md:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-6">
          <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary/10 text-primary font-body text-base md:text-lg font-extrabold mb-1.5 border border-primary/20 animate-pulse">
            <BarChart2 className="w-5 h-5" />{lang === 'ar' ? 'آخر المستجدات' : (lang === 'zgh' || lang === 'amz' ? 'ⵜⵓⵙⵙⵏⴰ ⵜⴰⵎⴰⵜⴰⵢⵜ' : 'Live Dashboard')}
          </span>
          <div className="flex items-center justify-center gap-4 mb-2">
            <h2 className="text-3xl md:text-5xl font-display font-black tracking-tight text-foreground text-center">
              {lang === "ar" ? config.titleAr : (lang === "zgh" || lang === "amz" ? config.titleAmz : config.titleFr)}
            </h2>
          </div>
          <p className="text-muted-foreground font-body text-center max-w-2xl mx-auto">
            {lang === "ar" ? config.subtitleAr : (lang === "zgh" || lang === "amz" ? config.subtitleAmz : config.subtitleFr)}
          </p>
          <button onClick={refresh} className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors">
            <RefreshCw className="w-3.5 h-3.5" />
            Mis à jour : {lastUpdate.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </button>
        </motion.div>

        <p className="text-center text-sm text-muted-foreground mb-3 italic">
          {lang === "ar" ? "اضغط للاطلاع على التفاصيل" : (lang === "zgh" || lang === "amz" ? "ⴰⴷⵔ ⴰⴼⴰⴷ ⴰⴷ ⵜⵥⵕⵜ" : "Cliquez pour consulter")}
        </p>

        <div className="flex flex-wrap gap-2 mb-6 justify-center">
          {TABS.map(tab => {
            const tabConfig = config.tabs.find(t => t.id === tab.id);
            if (tabConfig && !tabConfig.visible) return null;

            const Icon = tab.icon;
            const label = lang === "ar" ? (tabConfig?.labelAr || tab.labelAr) : 
                          (lang === "zgh" || lang === "amz" ? (tabConfig?.labelAmz || tab.labelAmz || tab.label) : 
                          (tabConfig?.labelFr || tab.label));
            const isPending = config.status === "pending";

            return (
              <button
                key={tab.id}
                onClick={() => { if (!isPending) setActiveTab(activeTab === tab.id ? "" : tab.id) }}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-body font-semibold transition-all duration-200 border ${
                  isPending ? "bg-muted/50 text-muted-foreground border-border/50 cursor-not-allowed opacity-70"
                  : activeTab === tab.id
                    ? "bg-primary text-primary-foreground border-primary shadow-md"
                    : "bg-card text-foreground border-border hover:border-primary/50 hover:text-primary"
                }`}
                disabled={isPending}
              >
                <Icon className="w-4 h-4" />{label}
              </button>
            );
          })}
        </div>

        {config.status === "pending" ? (
          <div className="mt-8 text-center">
            <div className="relative inline-flex items-center justify-center w-full sm:w-auto overflow-hidden rounded-full shadow-lg shadow-amber-500/20 transition-transform hover:scale-[1.02]">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-400 via-orange-500 to-red-500 animate-gradient-x opacity-90"></div>
              <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-100 transition-opacity"></div>
              
              <div className="relative z-10 flex items-center justify-center min-h-[3.5rem] whitespace-normal text-sm sm:text-lg px-6 sm:px-10 py-3 sm:py-5 font-display font-extrabold gap-2 sm:gap-3 text-white">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm shrink-0">
                  <Clock className="w-4 h-4 text-white animate-pulse" />
                </div>
                <span>
                  {lang === 'ar' ? 'البارومتر قيد التجميع... ابقوا معنا!' : 
                   (lang === 'zgh' || lang === 'amz' ? 'ⴰⵙⵖⴰⵍ ⴰⴷ ⴰⵔ ⵉⵜⵜⵓⵙⵎⵓⵏ... ⵇⵇⵉⵎⴰⵜ ⴷⵉⵏⵏⴰ!' : 
                   'Le baromètre est en cours de consolidation... Stay tuned !')}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
              {activeTab === "party"    && <ByPartyTab   data={barometerData.byParty} onClose={handleCloseTab} lang={lang} />}
              {activeTab === "region"   && <ByRegionTab  data={barometerData.byRegion} onClose={handleCloseTab} lang={lang} />}
              {activeTab === "gender"   && <GenderTab    data={barometerData.byParty} onClose={handleCloseTab} lang={lang} />}
              {activeTab === "age"      && <AgeTab       globalAge={barometerData.globalAge} onClose={handleCloseTab} lang={lang} />}
              {activeTab === "sociopro" && <SocioProTab  globalSociopro={barometerData.globalSociopro} onClose={handleCloseTab} lang={lang} />}
            </motion.div>

            {activeTab && (
              <div className="mt-6 flex flex-wrap justify-center gap-4 text-[11px] text-muted-foreground border-t border-border pt-4">
                <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-emerald-500 inline-block" />{lang === 'ar' ? '≥ 70% قوي' : (lang === 'zgh' || lang === 'amz' ? '≥ 70% ⵉⴷⵓⵙ' : '≥ 70% Fort')}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-amber-500 inline-block" />{lang === 'ar' ? '40–69% متوسط' : (lang === 'zgh' || lang === 'amz' ? '40–69% ⴰⵏⴰⵎⵎⴰⵙ' : '40–69% Moyen')}</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-full bg-red-500 inline-block" />{lang === 'ar' ? '< 40% ضعيف' : (lang === 'zgh' || lang === 'amz' ? '< 40% ⵉⴷⵔⵓⵙ' : '< 40% Faible')}</span>
                <span className="flex items-center gap-1.5 text-emerald-600 font-semibold">{lang === 'ar' ? '● بيانات مستمدة من تجميع التزكيات' : (lang === 'zgh' || lang === 'amz' ? '● ⵉⵙⴼⴽⴰ ⵙⴳ ⵓⵙⵎⵓⵏ ⵏ ⵉⵙⵖⵣⴰⵏ' : '● Données issues des consolidations des accréditations')}</span>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default AccreditationBarometers;
